import { useEffect, useMemo, useState, useRef } from 'react';
import facilityHero1 from './assets/Salinan_Front_View.jpg';
import facilityHero2 from './assets/Drop_Off_Area_Day.jpg';
import facilityHero3 from './assets/tampak_atas.jpg';
import campusAuditorium from './assets/Salinan_Auditorium.png';
import campusBasketball from './assets/Salinan_Basketball_Court.png';
import campusCanteen from './assets/Salinan_Canteen.png';
import campusClassroom from './assets/Salinan_Classroom.png';
import campusLabBio from './assets/Salinan_Lab_Bio.png';
import campusLibrary from './assets/Salinan_Library.png';
import campusMusicRoom from './assets/Salinan_Music_Room.png';
import campusPlayground from './assets/Salinan_Playground_Rooftop.png';
import techLabs from './assets/Tech_Labs.png';
import techMusicRoom from './assets/Tech_Music_Room.png';
import techSmartClassroom from './assets/Tech_Smart_Classroom.png';
import safetyCCTV from './assets/Safety_CCTV.png';
import safetyHealthProtocol from './assets/Safety_Health_Protocol.png';
import safetyUKS from './assets/Safety_UKS.png';
import photoSmartClassroom from './assets/Photo_Smart_Classroom.png';
import photoLabs from './assets/Photo_Labs.png';
import photoMusicRoom from './assets/Photo_Music_Room.png';
import virtualTourLab from './assets/Virtual_Tour_Lab.png';
import facilityCard1 from './assets/news/news3.jpg';
import facilityCard2 from './assets/news/news5.jpg';
import facilityCard3 from './assets/news/news6.jpg';
import facilityCard4 from './assets/news/news8.jpg';

function FacilitiesPage({ page, onNavigate }) {
  const heroSlides = page?.hero_slides || [facilityHero1, facilityHero2, facilityHero3];
  const [activeHero, setActiveHero] = useState(0);
  const [activeCampus, setActiveCampus] = useState(0);
  const [activeGallery, setActiveGallery] = useState(0);
  const [facilitiesCardPage, setFacilitiesCardPage] = useState(0); // For 4-card carousel
  const facilitiesGridRef = useRef(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveHero((current) => (current + 1) % heroSlides.length);
    }, 6000);

    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.facility-animate');
    if (!animatedItems.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    animatedItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const defaultOverview = [
    {
      id: 'auditorium',
      title: 'Auditorium',
      description:
        'A versatile performance and assembly space equipped for school events, presentations, and cultural celebrations.',
      image: campusAuditorium,
    },
    {
      id: 'basketball-court',
      title: 'Basketball Court',
      description:
        'Indoor and outdoor courts for basketball training, competitive games, and athletic development programs.',
      image: campusBasketball,
    },
    {
      id: 'canteen',
      title: 'Canteen',
      description:
        'A nutritious dining facility offering healthy meals and snacks to support student wellness and social interaction.',
      image: campusCanteen,
    },
    {
      id: 'classroom',
      title: 'Classroom',
      description:
        'Bright, tech-ready classrooms with flexible seating to support collaboration and focused learning.',
      image: campusClassroom,
    },
    {
      id: 'biology-lab',
      title: 'Biology Lab',
      description:
        'Well-equipped laboratory for biology experiments, microscopy, and life sciences hands-on learning.',
      image: campusLabBio,
    },
    {
      id: 'library',
      title: 'Library',
      description:
        'A welcoming reading hub with curated collections, quiet study corners, and digital resources.',
      image: campusLibrary,
    },
    {
      id: 'music-room',
      title: 'Music Room',
      description:
        'Creative studio for music rehearsal, performance practice, and musical expression learning experiences.',
      image: campusMusicRoom,
    },
    {
      id: 'playground',
      title: 'Playground',
      description:
        'Rooftop recreational space for outdoor activities, team building, and physical exercise programs.',
      image: campusPlayground,
    },
  ];

  const fallbackImages = useMemo(
    () => [facilityCard1, facilityCard2, facilityCard3, facilityCard4],
    []
  );
  const rawOverview = page?.campus_overview || defaultOverview;
  const campusOverview = useMemo(
    () =>
      rawOverview.map((item, index) => {
        if (typeof item === 'string') {
          return {
            id: `facility-${index + 1}`,
            title: item,
            description: 'Facility information will be updated soon.',
            image: fallbackImages[index % fallbackImages.length],
          };
        }

        const title = item?.title || `Facility ${index + 1}`;
        const normalizedId = String(item?.id || title)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');

        return {
          id: normalizedId || `facility-${index + 1}`,
          title,
          description: item?.description || 'Facility information will be updated soon.',
          image: item?.image || fallbackImages[index % fallbackImages.length],
        };
      }),
    [rawOverview, fallbackImages]
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveCampus((current) => (current + 1) % campusOverview.length);
    }, 7000);

    return () => clearInterval(intervalId);
  }, [campusOverview.length]);

  const defaultTechnology = [
    {
      id: 'smart-classroom',
      title: 'Smart Classroom',
      description:
        'Interactive displays, digital resources, and blended learning tools to enrich daily lessons. Teachers leverage technology to create engaging, personalized learning experiences.',
      image: techSmartClassroom,
    },
    {
      id: 'it-lab',
      title: 'IT Lab',
      description:
        'Dedicated computing space for coding, research, and digital literacy projects. Students develop essential tech skills and explore careers in technology and innovation.',
      image: techLabs,
    },
    {
      id: 'music-room',
      title: 'Music Room',
      description:
        'Creative studio for music rehearsal, performance practice, and musical expression learning experiences.',
      image: techMusicRoom,
    },
  ];

  const rawTechnology = page?.technology_innovation || defaultTechnology;
  const technologyInnovation = useMemo(
    () =>
      rawTechnology.map((item, index) => {
        if (typeof item === 'string') {
          return {
            id: `technology-${index + 1}`,
            title: item,
            description: 'Facility information will be updated soon.',
            image: fallbackImages[index % fallbackImages.length],
          };
        }

        const title = item?.title || `Technology ${index + 1}`;
        const normalizedId = String(item?.id || title)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');

        return {
          id: normalizedId || `technology-${index + 1}`,
          title,
          description: item?.description || 'Facility information will be updated soon.',
          image: item?.image || fallbackImages[index % fallbackImages.length],
        };
      }),
    [rawTechnology, fallbackImages]
  );

  const defaultSafety = [
    {
      id: 'health-protocols',
      title: 'Health Protocols',
      description:
        'Daily sanitation, wellness checks, and readiness procedures to keep students safe and healthy. Comprehensive health measures ensure a secure learning environment.',
      image: safetyHealthProtocol,
    },
    {
      id: 'cctv',
      title: 'CCTV Monitoring',
      description:
        'Campus monitoring across key areas with continuous oversight and quick response procedures. Advanced security systems protect our school community.',
      image: safetyCCTV,
    },
    {
      id: 'school-nurse',
      title: 'School Nurse',
      description:
        'On-site nurse support for first aid, health guidance, and parent coordination. Professional medical care available throughout the school day.',
      image: safetyUKS,
    },
  ];

  // Hardcode Safety & Security to always use local images
  const safetySecurity = defaultSafety;

  const defaultGallery = [
    {
      id: 'gallery-classrooms',
      title: 'Smart Classroom',
      description: 'Bright learning spaces with interactive technology that support collaboration, focused study, and modern educational engagement.',
      image: photoSmartClassroom,
    },
    {
      id: 'gallery-labs',
      title: 'Science & ICT Labs',
      description: 'Hands-on experiments and digital exploration in modern labs where students discover, innovate, and develop critical thinking skills.',
      image: photoLabs,
    },
    {
      id: 'gallery-arts',
      title: 'Music Room',
      description: 'Creative space for music, performance, and student showcases where talent flourishes and self-expression is celebrated.',
      image: photoMusicRoom,
    },
  ];

  // Hardcode Photo Gallery to always use local images
  const photoGallery = defaultGallery;

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveGallery((current) => (current + 1) % photoGallery.length);
    }, 3000);

    return () => clearInterval(intervalId);
  }, [photoGallery.length]);

  // Hardcode Virtual Tour to always use local image
  const virtualTour = {
    title: 'Virtual Tour Experience',
    summary:
      'Explore classrooms, labs, and campus facilities with a guided virtual tour from anywhere.',
    ctaLabel: 'Start Virtual Tour',
    ctaHref: '#',
    image: virtualTourLab,
  };

  return (
    <section className="facilities-page">
      <section className="admissions-hero" id="facilities-overview">
        <div className="admissions-hero-media" aria-hidden="true">
          {heroSlides.map((slide, index) => (
            <div
              key={slide}
              className={`admissions-hero-slide ${index === activeHero ? 'active' : ''}`}
            >
              <img src={slide} alt="Facilities campus" />
            </div>
          ))}
        </div>
        <div className="admissions-hero-overlay" aria-hidden="true" />
        <div className="admissions-hero-content">
          <p className="admissions-hero-kicker">Facilities</p>
          <h2>{page?.title || 'Campus Facilities'}</h2>
          <p>
            {page?.summary ||
              'Explore learning spaces designed to nurture academic excellence, creativity, and student wellbeing.'}
          </p>
          <div className="admissions-hero-dots" role="tablist" aria-label="Facilities hero slides">
            {heroSlides.map((slide, index) => (
              <button
                key={slide}
                type="button"
                className={`admissions-hero-dot ${index === activeHero ? 'active' : ''}`}
                onClick={() => setActiveHero(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="facilities-campus" id="campus-overview">
        <div className="facilities-campus-container">
          {/* Featured Facility Showcase - Header + Slideshow Side by Side */}
          <div className="facilities-featured-showcase facility-animate">
            {/* Left: Header */}
            <div className="facilities-showcase-header">
              <p className="facilities-campus-kicker">Campus Overview</p>
              <h2>Explore Our Facilities</h2>
              <p>
                State-of-the-art learning spaces designed to support academic excellence, creativity, and student wellbeing. Each facility is thoughtfully designed to inspire learning and foster growth.
              </p>
            </div>

            {/* Right: Slideshow */}
            <div className="facilities-showcase-image">
              <div className="facilities-showcase-wrapper">
                <div className="facilities-showcase-track" style={{ transform: `translateX(-${activeCampus * 100}%)` }}>
                  {campusOverview.map((item) => (
                    <div key={item.id} className="facilities-showcase-slide">
                      <img src={item.image} alt={item.title} />
                      <div className="facilities-showcase-overlay" />
                      {/* Overlay Text */}
                      <div className="facilities-showcase-text-overlay">
                        <p className="facilities-showcase-text-label">{String(item.id).toUpperCase().replace(/-/g, ' ')}</p>
                        <h3>{item.title}</h3>
                        <p>{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Controls */}
                <div className="facilities-showcase-controls">
                  <button
                    type="button"
                    className="facilities-showcase-arrow left"
                    onClick={() => setActiveCampus((activeCampus - 1 + campusOverview.length) % campusOverview.length)}
                    aria-label="Previous facility"
                  >
                    <i className="fas fa-chevron-left" />
                  </button>
                  <button
                    type="button"
                    className="facilities-showcase-arrow right"
                    onClick={() => setActiveCampus((activeCampus + 1) % campusOverview.length)}
                    aria-label="Next facility"
                  >
                    <i className="fas fa-chevron-right" />
                  </button>
                </div>
              </div>
              {/* Dots Indicator */}
              <div className="facilities-showcase-dots">
                {[0, 1, 2].map((dotIndex) => (
                  <button
                    key={`dot-${dotIndex}`}
                    type="button"
                    className={`facilities-showcase-dot ${
                      Math.floor(activeCampus / 3) === dotIndex ? 'active' : ''
                    }`}
                    onClick={() => setActiveCampus(dotIndex * 3)}
                    aria-label={`Show facility group ${dotIndex + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Facilities Cards - 4-Card Carousel */}
          <div className="facilities-cards-section facility-animate">
            <div className="facilities-cards-wrapper">
              {/* Left Arrow */}
              <button
                type="button"
                className="facilities-cards-nav-arrow left"
                onClick={() => setFacilitiesCardPage(Math.max(0, facilitiesCardPage - 1))}
                disabled={facilitiesCardPage === 0}
                aria-label="Previous cards"
              >
                <i className="fas fa-chevron-left" />
              </button>

              {/* Cards Grid - Show 4 at a time */}
              <div className="facilities-cards-carousel">
                {campusOverview.slice(facilitiesCardPage * 4, facilitiesCardPage * 4 + 4).map((item, index) => (
                  <article
                    key={item.id}
                    className="facilities-card"
                    onClick={() => onNavigate && onNavigate(`facility-${item.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        onNavigate && onNavigate(`facility-${item.id}`);
                      }
                    }}
                  >
                    <div className="facilities-card-image">
                      <img src={item.image} alt={item.title} />
                      <div className="facilities-card-overlay" />
                      <span className="facilities-card-number">0{facilitiesCardPage * 4 + index + 1}</span>
                    </div>
                    <div className="facilities-card-content">
                      <h4>{item.title}</h4>
                      <p>{item.description}</p>
                      <div className="facilities-card-arrow">
                        <i className="fas fa-arrow-right" />
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Right Arrow */}
              <button
                type="button"
                className="facilities-cards-nav-arrow right"
                onClick={() => setFacilitiesCardPage(Math.min(Math.ceil(campusOverview.length / 4) - 1, facilitiesCardPage + 1))}
                disabled={facilitiesCardPage >= Math.ceil(campusOverview.length / 4) - 1}
                aria-label="Next cards"
              >
                <i className="fas fa-chevron-right" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="facilities-technology" id="technology-innovation">
        <div className="facilities-campus-header">
          <p className="facilities-campus-kicker">Technology & Innovation</p>
          <h3>Learning Spaces for the Future</h3>
          <p>
            Purpose-built technology environments that empower students to innovate, collaborate, and develop skills for tomorrow's world.
          </p>
        </div>

        <div className="facilities-tech-grid">
          {technologyInnovation.map((item) => (
            <article
              key={item.id}
              id={item.id}
              className="facility-tech-card facility-animate"
              onClick={() => onNavigate && onNavigate(`technology-${item.id}`)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onNavigate && onNavigate(`technology-${item.id}`);
                }
              }}
            >
              <div className="facility-tech-media">
                <img src={item.image} alt={item.title} />
                <span className="facility-tech-badge">Innovation</span>
              </div>
              <div className="facility-tech-body">
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="facilities-safety" id="safety-security">
        <div className="facilities-campus-header">
          <p className="facilities-campus-kicker">Safety & Security</p>
          <h3>A Secure Learning Environment</h3>
          <p>
            Comprehensive safety measures and proactive health practices ensure our students learn in a secure, supportive, and healthy environment every day.
          </p>
        </div>

        <div className="facilities-safety-grid">
          {safetySecurity.map((item, index) => (
            <article
              key={item.id}
              id={item.id}
              className="facility-safety-card facility-animate"
            >
              <div className="facility-safety-index">0{index + 1}</div>
              <div className="facility-safety-media">
                <img src={item.image} alt={item.title} />
              </div>
              <div className="facility-safety-body">
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="facilities-gallery" id="photo-gallery">
        <div className="facilities-campus-header">
          <p className="facilities-campus-kicker">Photo Gallery</p>
          <h3>Moments Across Our Campus</h3>
          <p>
            Explore the vibrant life and learning happening every day across our campus spaces. From classrooms to labs, arts to athletics, discover the heart of our school community.
          </p>
        </div>

        <div className="facilities-gallery-slideshow">
          {photoGallery.map((item, index) => (
            <article
              key={item.id}
              className={`facility-gallery-slide ${index === activeGallery ? 'active' : ''}`}
            >
              <div className="facility-gallery-media">
                <img src={item.image} alt={item.title} />
              </div>
              <div className="facility-gallery-overlay" />
              <div className="facility-gallery-body">
                <h4>{item.title}</h4>
              </div>
            </article>
          ))}
          
          {/* Gallery Controls */}
          <div className="facilities-gallery-controls">
            <button
              type="button"
              className="gallery-arrow-btn"
              onClick={() => setActiveGallery((activeGallery - 1 + photoGallery.length) % photoGallery.length)}
              aria-label="Previous gallery image"
            >
              <i className="fas fa-chevron-left" />
            </button>
            <button
              type="button"
              className="gallery-arrow-btn"
              onClick={() => setActiveGallery((activeGallery + 1) % photoGallery.length)}
              aria-label="Next gallery image"
            >
              <i className="fas fa-chevron-right" />
            </button>
          </div>

          {/* Gallery Dots */}
          <div className="facilities-gallery-dots">
            {photoGallery.map((item, index) => (
              <button
                key={item.id}
                type="button"
                className={`gallery-dot ${index === activeGallery ? 'active' : ''}`}
                onClick={() => setActiveGallery(index)}
                aria-label={`Show ${item.title}`}
              />
            ))}
          </div>
        </div>

        <div className="facility-tour" id="virtual-tour">
          <div className="facility-tour-media" aria-hidden="true">
            <img src={virtualTour.image} alt="Virtual tour preview" />
          </div>
          <div className="facility-tour-body">
            <p className="facility-tour-kicker">Virtual Tour</p>
            <h4>{virtualTour.title}</h4>
            <p>{virtualTour.summary}</p>
            <a className="facility-tour-cta" href={virtualTour.ctaHref}>
              {virtualTour.ctaLabel} <i className="fas fa-arrow-right" />
            </a>
          </div>
        </div>

        {/* Contact Us CTA Section */}
        <div className="facility-contact-cta" id="contact-us-cta">
          <div className="facility-contact-content">
            <h3>Have Questions About Our Facilities?</h3>
            <p>Contact us today to schedule a campus tour or learn more about our world-class learning environment.</p>
            <button 
              type="button"
              className="facility-contact-button"
              onClick={() => onNavigate && onNavigate('contact-us')}
            >
              Contact Us <i className="fas fa-arrow-right" />
            </button>
          </div>
        </div>
      </section>
    </section>
  );
}

export default FacilitiesPage;
